import { Badge } from "@/components/ui/badge";
import { DocumentInfo, DocumentStatus } from "@/types/document";
import {
  Archive,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  LoaderCircle,
  ShieldAlert,
} from "lucide-react";

interface DocumentBadgeProps {
  status: DocumentStatus;
  onClick?: (e: React.MouseEvent) => void;
  progress?: DocumentInfo["progress"];
  grade?: string;
}

export const DocumentBadge = ({
  status,
  onClick,
  progress,
  grade,
}: DocumentBadgeProps) => {
  const getBadgeConfig = (status: DocumentStatus) => {
    switch (status) {
      case "importing": {
        const percentage =
          progress && progress.total > 0
            ? Math.round((progress.current / progress.total) * 100)
            : 0;
        return {
          label: percentage === 0 ? "Queued" : `${percentage}%`,
          icon:
            percentage === 0 ? null : (
              <LoaderCircle className="w-4 h-4 animate-spin" />
            ),
          className: "font-semibold",
          variant: "outline",
        };
      }
      case "ready":
        return {
          label: "Todo",
          icon: <CircleDashed className="w-5 h-5" />,
          className:
            "bg-secondary font-semibold text-secondary-foreground border-1 border-transparent",
          variant: "outline",
        };
      case "in_progress":
        return {
          label: "In progress",
          icon: <CircleDot className="w-5 h-5" />,
          className: "font-semibold",
          variant: "outline",
        };
      case "done":
        if (grade) {
          return {
            label: grade,
            icon: null,
            className: "bg-primary font-semibold text-primary-foreground",
            variant: "outline",
          };
        }
        return {
          label: "Graded",
          icon: (
            <CircleCheck className="w-5 h-5 stroke-white font-bold text-white" />
          ),
          className: "bg-green-600 font-semibold text-white",
          variant: "outline",
        };
      case "failed":
        return {
          label: "Failed",
          icon: <CircleAlert className="w-4 h-4" />,
          className: "bg-orange-50 font-semibold text-orange-500",
          variant: "outline",
        };
      case "flagged":
        return {
          label: "Flagged",
          icon: <ShieldAlert className="w-4 h-4" />,
          className: "bg-red-500 font-semibold text-white",
          variant: "outline",
        };
      case "archived":
        return {
          label: "Archived",
          icon: <Archive className="w-4 h-4" />,
          className: "bg-gray-500 font-semibold text-white",
          variant: "outline",
        };
      default:
        return {
          label: "Unknown",
          icon: <CircleAlert className="w-4 h-4" />,
          className: "bg-gray-100 font-semibold text-gray-600",
          variant: "outline",
        };
    }
  };

  const config = getBadgeConfig(status);
  return (
    <Badge
      variant={config.variant as "secondary" | "outline"}
      data-test-role="document-status"
      className={`${config.className} ${
        onClick
          ? "cursor-pointer hover:opacity-80 transition-opacity"
          : "cursor-default"
      }`}
      onClick={onClick}
    >
      {config.label}
      {config.icon}
    </Badge>
  );
};
